<template>
	<div class="animated fadeIn board">

		<!-- Header -->
		<div class="board-head">
			<div>
				<h4 class="mb-0">System status</h4>
				<small class="text-muted">
					Checked automatically every {{ refreshSeconds }} seconds
				</small>
			</div>
			<div class="board-head-actions">
				<span class="next-check" :class="{soon: countdown <= 10}">Next check in {{ countdown }}s</span>
				<b-button size="sm" variant="outline-secondary" @click="load" :disabled="loading">
					<i class="icon-refresh mr-1"></i>Check now
				</b-button>
			</div>
		</div>

		<div v-if="loading && !loaded" class="text-center py-5">
			<b-spinner variant="info" style="width:3rem;height:3rem;"></b-spinner>
		</div>

		<div v-else>
			<b-alert v-if="loadError" show variant="warning" class="small">{{ loadError }}</b-alert>

			<template v-else>
				<!-- The one line anyone can read -->
				<section class="verdict" :class="'verdict-' + verdict.tone" role="status" aria-live="polite">
					<span class="verdict-mark" aria-hidden="true">{{ verdict.mark }}</span>
					<div class="verdict-text">
						<p class="verdict-line">{{ verdict.headline }}</p>
						<p class="verdict-sub">{{ verdict.detail }}</p>
					</div>
				</section>

				<!-- Watched systems, grouped by what people call them -->
				<section v-for="group in watchedGroups" :key="group.key" class="group">
					<h2 class="group-title">
						{{ group.label }}
						<span class="group-count">{{ group.systems.length }}</span>
					</h2>
					<div class="tiles">
						<article v-for="s in group.systems" :key="s.code"
										 class="tile" :class="'tile-' + s.status"
										 :aria-label="s.name + ': ' + stateWord(s.status)">
							<div class="tile-state">
								<span class="tile-mark" aria-hidden="true">{{ mark(s.status) }}</span>
								<span class="tile-word">{{ stateWord(s.status) }}</span>
							</div>
							<h3 class="tile-name">{{ s.name }}</h3>
							<p class="tile-metric">{{ tileMetric(s) }}</p>
							<p v-if="s.message" class="tile-note" :title="s.message">{{ s.message }}</p>
						</article>
					</div>
				</section>

				<!-- Systems nobody is watching yet -->
				<section v-if="notSetUp.length" class="group">
					<h2 class="group-title">
						Not set up yet
						<span class="group-count">{{ notSetUp.length }}</span>
					</h2>
					<p class="group-note">
						These have no health address, so nothing is being checked. Add one from
						<router-link to="/monitoring/systems">Systems</router-link> and they join the board above.
					</p>
					<div class="tiles">
						<article v-for="s in notSetUp" :key="s.code" class="tile tile-NOT_CONFIGURED"
										 :aria-label="s.name + ': not set up'">
							<div class="tile-state">
								<span class="tile-mark" aria-hidden="true">○</span>
								<span class="tile-word">Not set up</span>
							</div>
							<h3 class="tile-name">{{ s.name }}</h3>
							<p class="tile-metric">No health address</p>
						</article>
					</div>
				</section>

				<!-- Everything an engineer needs, out of everyone else's way -->
				<section class="detail">
					<button type="button" class="detail-toggle" @click="showDetail = !showDetail"
									:aria-expanded="showDetail ? 'true' : 'false'">
						<span class="detail-caret" :class="{open: showDetail}" aria-hidden="true">▸</span>
						Technical detail
					</button>

					<b-collapse v-model="showDetail" id="tech-detail">
						<div class="detail-body">
							<h3 class="detail-heading">Response time, last 24 hours</h3>
							<p v-if="!trendSeriesCount" class="text-muted small">No response times recorded yet.</p>
							<canvas v-show="trendSeriesCount" ref="chart" height="70"></canvas>

							<h3 class="detail-heading mt-4">Recent checks</h3>
							<div class="table-responsive">
								<table class="table table-sm mb-0">
									<thead>
										<tr>
											<th>System</th>
											<th>Result</th>
											<th class="text-right">Response</th>
											<th>Type</th>
											<th>When</th>
											<th>Message</th>
										</tr>
									</thead>
									<tbody>
										<tr v-for="e in recentEvents" :key="e.id">
											<td><code>{{ e.service_name }}</code></td>
											<td>{{ stateWord(e.status) }}</td>
											<td class="text-right num">
												<span v-if="e.response_time_ms !== null && e.response_time_ms !== undefined">{{ e.response_time_ms }} ms</span>
												<span v-else class="text-muted">—</span>
											</td>
											<td class="small">{{ e.entry_type }}</td>
											<td class="small text-muted">{{ formatDateTime(e.checked_at) }}</td>
											<td class="small text-muted">{{ e.message || '—' }}</td>
										</tr>
										<tr v-if="!recentEvents.length">
											<td colspan="6" class="text-center text-muted py-4">Nothing recorded yet.</td>
										</tr>
									</tbody>
								</table>
							</div>
						</div>
					</b-collapse>
				</section>
			</template>
		</div>
	</div>
</template>

<script>
import Chart from 'chart.js';
import monitoringService from '@/services/monitoringService';

const REFRESH_SECONDS = 30;

// Plain words, because this board is read by support, sales and accounts as well as engineers.
// "DOWN" is jargon; "Not responding" is what actually happened.
const STATE_WORDS = {
	UP: 'Working',
	DEGRADED: 'Slow',
	DOWN: 'Not responding',
	NO_DATA: 'Waiting for first check',
	NOT_CONFIGURED: 'Not set up'
};

// A shape as well as a colour, so the board survives greyscale printing and colour blindness —
// which is the difference between "every department can read it" and "most of them can".
const STATE_MARKS = {
	UP: '●',
	DEGRADED: '▲',
	DOWN: '■',
	NO_DATA: '◌',
	NOT_CONFIGURED: '○'
};

// Category codes are internal; these are the names people use for the same things.
const GROUP_LABELS = {
	HRM: 'HRM systems',
	CS: 'Device Portal systems',
	ADMIN: 'Admin systems',
	OTHER: 'Other systems'
};
const GROUP_ORDER = ['HRM', 'CS', 'ADMIN', 'OTHER'];

const PALETTE = [
	'27,122,80', '154,102,6', '179,59,46', '46,109,164', '116,79,150',
	'22,132,127', '176,106,44', '90,107,120', '58,141,94', '148,66,101'
];

export default {
	name: 'LiveMonitoring',

	data() {
		return {
			refreshSeconds: REFRESH_SECONDS,
			loading: false,
			loaded: false,
			loadError: '',
			countdown: REFRESH_SECONDS,
			showDetail: false,

			systems: [],
			counts: {},
			total: 0,
			recentEvents: [],
			trend: {},
			lastRefreshedAt: null,

			chartInst: null,
			timer: null,
			ticker: null
		};
	},

	computed: {
		watched() {
			return this.systems.filter(s => s.status !== 'NOT_CONFIGURED');
		},

		notSetUp() {
			return this.systems.filter(s => s.status === 'NOT_CONFIGURED');
		},

		watchedGroups() {
			const buckets = {};
			this.watched.forEach(s => {
				const key = GROUP_LABELS[s.category] ? s.category : 'OTHER';
				(buckets[key] = buckets[key] || []).push(s);
			});

			// Inside a group, anything wrong comes first — the eye should land on a problem without
			// having to scan.
			const rank = {DOWN: 0, DEGRADED: 1, NO_DATA: 2, UP: 3};
			return GROUP_ORDER
				.filter(key => buckets[key] && buckets[key].length)
				.map(key => ({
					key: key,
					label: GROUP_LABELS[key],
					systems: buckets[key].sort((a, b) => {
						const d = (rank[a.status] || 9) - (rank[b.status] || 9);
						return d !== 0 ? d : (a.name || '').localeCompare(b.name || '');
					})
				}));
		},

		verdict() {
			const down = this.counts.DOWN || 0;
			const slow = this.counts.DEGRADED || 0;
			const up = this.counts.UP || 0;
			const waiting = this.counts.NO_DATA || 0;
			const unset = this.counts.NOT_CONFIGURED || 0;
			const watched = up + slow + down + waiting;

			const checkedNote = this.lastRefreshedAt
				? 'Checked ' + this.relativeTime(this.lastRefreshedAt) + '.'
				: '';
			const setupNote = unset ? ' ' + unset + ' more not set up yet.' : '';

			if (down > 0) {
				return {
					tone: 'DOWN',
					mark: STATE_MARKS.DOWN,
					headline: down === 1 ? '1 system is not responding' : down + ' systems are not responding',
					detail: up + ' of ' + watched + ' working. ' + checkedNote + setupNote
				};
			}
			if (slow > 0) {
				return {
					tone: 'DEGRADED',
					mark: STATE_MARKS.DEGRADED,
					headline: slow === 1 ? '1 system is running slow' : slow + ' systems are running slow',
					detail: up + ' of ' + watched + ' responding normally. ' + checkedNote + setupNote
				};
			}
			if (watched > 0 && up === watched) {
				return {
					tone: 'UP',
					mark: STATE_MARKS.UP,
					headline: 'Everything is working',
					detail: 'All ' + watched + ' systems responded. ' + checkedNote + setupNote
				};
			}
			if (watched > 0) {
				return {
					tone: 'NO_DATA',
					mark: STATE_MARKS.NO_DATA,
					headline: 'Waiting for the first check',
					detail: watched + ' systems are set up but have not been checked yet.' + setupNote
				};
			}
			return {
				tone: 'NO_DATA',
				mark: STATE_MARKS.NO_DATA,
				headline: 'Nothing is being checked yet',
				detail: unset
					? unset + ' systems are listed, but none has a health address.'
					: 'No systems have been added.'
			};
		},

		trendSeriesCount() {
			return Object.keys(this.trend).filter(k => (this.trend[k] || []).length).length;
		}
	},

	watch: {
		showDetail(open) {
			// The canvas has no size while collapsed, so Chart.js must draw after it is visible.
			if (open) this.$nextTick(this.renderChart);
		}
	},

	created() {
		this.load();
		this.timer = setInterval(this.load, REFRESH_SECONDS * 1000);
		this.ticker = setInterval(() => {
			this.countdown = this.countdown > 0 ? this.countdown - 1 : REFRESH_SECONDS;
		}, 1000);
	},

	beforeDestroy() {
		clearInterval(this.timer);
		clearInterval(this.ticker);
		if (this.chartInst) this.chartInst.destroy();
	},

	methods: {
		load() {
			this.loading = true;
			this.countdown = REFRESH_SECONDS;
			this.loadError = '';

			monitoringService.getLive({trendHours: 24, events: 10}, (data) => {
				this.loading = false;
				this.loaded = true;

				if (!data || data.available === false) {
					this.loadError = (data && data.error) || 'The status board is not available right now.';
					return;
				}

				this.systems = data.systems || [];
				this.counts = data.counts || {};
				this.total = data.total || 0;
				this.recentEvents = data.recentEvents || [];
				this.trend = data.trend || {};
				this.lastRefreshedAt = new Date();

				if (this.showDetail) this.$nextTick(this.renderChart);
			}, (err) => {
				this.loading = false;
				this.loaded = true;
				this.loadError = err;
			});
		},

		stateWord(state) {
			return STATE_WORDS[state] || state || 'Unknown';
		},

		mark(state) {
			return STATE_MARKS[state] || '○';
		},

		tileMetric(s) {
			if (s.status === 'UP' || s.status === 'DEGRADED') {
				return s.responseTimeMs !== null && s.responseTimeMs !== undefined
					? 'Replied in ' + this.formatMs(s.responseTimeMs)
					: 'Replied';
			}
			if (s.status === 'DOWN') {
				return s.checkedAt ? 'Since ' + this.relativeTime(s.checkedAt) : 'No reply';
			}
			return 'Not checked yet';
		},

		formatMs(ms) {
			const n = Number(ms);
			if (isNaN(n)) return ms + ' ms';
			return n >= 1000 ? (n / 1000).toFixed(1) + ' s' : n + ' ms';
		},

		relativeTime(value) {
			const then = value instanceof Date ? value : new Date(value);
			if (isNaN(then.getTime())) return String(value);

			const seconds = Math.max(0, Math.round((Date.now() - then.getTime()) / 1000));
			if (seconds < 60) return seconds + ' seconds ago';
			const minutes = Math.round(seconds / 60);
			if (minutes < 60) return minutes === 1 ? 'a minute ago' : minutes + ' minutes ago';
			const hours = Math.round(minutes / 60);
			if (hours < 24) return hours === 1 ? 'an hour ago' : hours + ' hours ago';
			return Math.round(hours / 24) + ' days ago';
		},

		formatDateTime(value) {
			if (!value) return '—';
			const d = new Date(value);
			return isNaN(d.getTime()) ? value : d.toLocaleString();
		},

		renderChart() {
			const ctx = this.$refs.chart;
			if (!ctx || !this.trendSeriesCount) return;

			const codes = Object.keys(this.trend).filter(k => (this.trend[k] || []).length);
			const datasets = codes.map((code, i) => {
				const rgb = PALETTE[i % PALETTE.length];
				return {
					label: this.nameFor(code),
					data: (this.trend[code] || []).map(p => ({x: p.checked_at, y: p.response_time_ms})),
					borderColor: 'rgba(' + rgb + ',1)',
					backgroundColor: 'rgba(' + rgb + ',.10)',
					fill: false,
					tension: 0.3,
					pointRadius: 2
				};
			});

			if (this.chartInst) {
				this.chartInst.data.datasets = datasets;
				this.chartInst.update();
				return;
			}

			this.chartInst = new Chart(ctx, {
				type: 'line',
				data: {datasets: datasets},
				options: {
					responsive: true,
					maintainAspectRatio: true,
					scales: {
						xAxes: [{type: 'time', time: {unit: 'hour'}, ticks: {maxTicksLimit: 12}}],
						yAxes: [{ticks: {beginAtZero: true, callback: v => v + ' ms'}}]
					},
					legend: {position: 'top'}
				}
			});
		},

		nameFor(code) {
			const match = this.systems.find(s => s.code === code);
			return match ? match.name : code;
		}
	}
};
</script>

<style scoped>
.board {
	/* Status is the palette here, so each state gets a deliberate ink / fill / edge trio rather
	   than a stock Bootstrap variant. */
	--ink: #16202a;
	--muted: #5d6d7b;
	--surface: #ffffff;

	--up-ink: #1b7a50;
	--up-fill: #e9f5ef;
	--up-edge: #b6dfcb;

	--slow-ink: #9a6606;
	--slow-fill: #fdf3e3;
	--slow-edge: #f0d9a8;

	--down-ink: #b33b2e;
	--down-fill: #fcedea;
	--down-edge: #f2c4bc;

	--wait-ink: #5a6b78;
	--wait-fill: #eff3f6;
	--wait-edge: #d3dde4;

	--unset-ink: #7c8895;
	--unset-edge: #ccd6dd;

	color: var(--ink);
}

/* ── Header ─────────────────────────────────────────────── */
.board-head {
	display: flex;
	flex-wrap: wrap;
	gap: 0.75rem;
	align-items: center;
	justify-content: space-between;
	margin-bottom: 1.25rem;
}

.board-head-actions {
	display: flex;
	align-items: center;
	gap: 0.75rem;
}

.next-check {
	font-size: 0.72rem;
	letter-spacing: 0.06em;
	text-transform: uppercase;
	color: var(--muted);
	font-variant-numeric: tabular-nums;
}

.next-check.soon { color: var(--slow-ink); }

/* ── Verdict: the one line anyone can read ──────────────── */
.verdict {
	display: flex;
	align-items: center;
	gap: 1.1rem;
	padding: 1.35rem 1.6rem;
	border: 1px solid var(--wait-edge);
	border-left: 6px solid var(--wait-ink);
	background: var(--surface);
	border-radius: 6px;
	margin-bottom: 2rem;
}

.verdict-mark {
	font-size: 1.6rem;
	line-height: 1;
	color: var(--wait-ink);
	flex: none;
}

/* Without this the flex child refuses to shrink and a long detail line pushes the card wide. */
.verdict-text { min-width: 0; }

.verdict-line {
	/* Light weight at large size reads calm rather than alarmed — the colour already carries the
	   urgency, and shouting twice just makes the board tiring to sit in front of. */
	font-size: 1.9rem;
	font-weight: 300;
	line-height: 1.15;
	letter-spacing: -0.02em;
	margin: 0;
}

.verdict-sub {
	margin: 0.3rem 0 0;
	font-size: 0.88rem;
	color: var(--muted);
}

/* Nothing is wrong and nothing is known — the neutral card, stated explicitly rather than left
   to fall through to the base rule by accident. */
.verdict-NO_DATA { border-left-color: var(--wait-ink); }

.verdict-UP { border-left-color: var(--up-ink); }
.verdict-UP .verdict-mark { color: var(--up-ink); }
.verdict-DEGRADED { border-left-color: var(--slow-ink); background: var(--slow-fill); }
.verdict-DEGRADED .verdict-mark { color: var(--slow-ink); }
.verdict-DOWN { border-left-color: var(--down-ink); background: var(--down-fill); }
.verdict-DOWN .verdict-mark { color: var(--down-ink); }

/* ── Groups ─────────────────────────────────────────────── */
.group { margin-bottom: 2rem; }

.group-title {
	display: flex;
	align-items: center;
	gap: 0.55rem;
	font-size: 0.72rem;
	font-weight: 700;
	letter-spacing: 0.14em;
	text-transform: uppercase;
	color: var(--muted);
	margin: 0 0 0.75rem;
}

.group-count {
	font-weight: 600;
	letter-spacing: 0;
	background: var(--wait-fill);
	border: 1px solid var(--wait-edge);
	border-radius: 999px;
	padding: 0.05rem 0.45rem;
	font-size: 0.7rem;
}

.group-note {
	font-size: 0.85rem;
	color: var(--muted);
	margin: -0.35rem 0 0.85rem;
	max-width: 60ch;
}

/* ── Tiles ──────────────────────────────────────────────── */
.tiles {
	display: grid;
	grid-template-columns: repeat(auto-fill, minmax(215px, 1fr));
	gap: 0.85rem;
}

.tile {
	background: var(--surface);
	border: 1px solid var(--wait-edge);
	border-top: 4px solid var(--wait-ink);
	border-radius: 6px;
	padding: 0.9rem 1rem 1rem;
	min-height: 128px;
	display: flex;
	flex-direction: column;
	gap: 0.3rem;
}

.tile-state {
	display: flex;
	align-items: center;
	gap: 0.4rem;
}

.tile-mark { font-size: 0.82rem; line-height: 1; }

.tile-word {
	font-size: 0.72rem;
	font-weight: 700;
	letter-spacing: 0.08em;
	text-transform: uppercase;
}

.tile-name {
	font-size: 1.02rem;
	font-weight: 600;
	margin: 0.15rem 0 0;
	line-height: 1.25;
}

.tile-metric {
	margin: 0;
	font-size: 0.85rem;
	color: var(--muted);
	font-variant-numeric: tabular-nums;
}

.tile-note {
	margin: 0.15rem 0 0;
	font-size: 0.76rem;
	color: var(--muted);
	overflow: hidden;
	text-overflow: ellipsis;
	display: -webkit-box;
	-webkit-line-clamp: 2;
	-webkit-box-orient: vertical;
}

.tile-UP { border-top-color: var(--up-ink); }
.tile-UP .tile-state { color: var(--up-ink); }

.tile-DEGRADED { border-top-color: var(--slow-ink); background: var(--slow-fill); }
.tile-DEGRADED .tile-state { color: var(--slow-ink); }

.tile-DOWN { border-top-color: var(--down-ink); background: var(--down-fill); }
.tile-DOWN .tile-state { color: var(--down-ink); }
.tile-DOWN .tile-name { color: var(--down-ink); }

.tile-NO_DATA { border-top-color: var(--wait-ink); }
.tile-NO_DATA .tile-state { color: var(--wait-ink); }

/* Not-set-up tiles are deliberately the quietest thing on the page: they are a to-do list, not a
   status. Dashed edge and no fill read as "absent" rather than "fine". */
.tile-NOT_CONFIGURED {
	background: transparent;
	border: 1px dashed var(--unset-edge);
	border-top: 1px dashed var(--unset-edge);
	min-height: 108px;
}
.tile-NOT_CONFIGURED .tile-state,
.tile-NOT_CONFIGURED .tile-name { color: var(--unset-ink); }
.tile-NOT_CONFIGURED .tile-name { font-weight: 500; }

/* The single moment of movement on the page, reserved for the one thing that needs a person. */
@media (prefers-reduced-motion: no-preference) {
	.tile-DOWN .tile-mark { animation: pulse 2.4s ease-in-out infinite; }
}

@keyframes pulse {
	0%, 100% { opacity: 1; }
	50% { opacity: 0.35; }
}

/* ── Technical detail ───────────────────────────────────── */
.detail {
	border-top: 1px solid var(--wait-edge);
	padding-top: 1rem;
}

.detail-toggle {
	background: none;
	border: 0;
	padding: 0;
	font-size: 0.72rem;
	font-weight: 700;
	letter-spacing: 0.14em;
	text-transform: uppercase;
	color: var(--muted);
	cursor: pointer;
	display: inline-flex;
	align-items: center;
	gap: 0.45rem;
}

.detail-toggle:focus-visible {
	outline: 2px solid var(--up-ink);
	outline-offset: 3px;
}

.detail-caret {
	display: inline-block;
	transition: transform 0.15s ease;
}
.detail-caret.open { transform: rotate(90deg); }

.detail-body { padding-top: 1rem; }

.detail-heading {
	font-size: 0.72rem;
	font-weight: 700;
	letter-spacing: 0.12em;
	text-transform: uppercase;
	color: var(--muted);
	margin-bottom: 0.6rem;
}

.num { font-variant-numeric: tabular-nums; }

@media (max-width: 480px) {
	.verdict { flex-direction: column; align-items: flex-start; gap: 0.6rem; }
	.verdict-line { font-size: 1.5rem; }
	.tiles { grid-template-columns: 1fr 1fr; }
}
</style>
